'use client';

import { usePathname } from 'next/navigation';
import type { ReactElement, ReactNode, RefObject } from 'react';
import { useRef } from 'react';

import { GTMViewItemList } from '../components/GTMViewItemList';
import { sendGTMEventEcommerce } from '../lib/events';
import { GTMEcommerceItem, LandingItemListName } from '../lib/types';

type LandingProduct = {
  sku?: string;
  heading?: string | ReactNode;
  name?: string;
  basePrice?: number;
  finalPrice?: number | null;
  volume?: string;
  brand?: string;
  series?: string;
  configurableVariants?: { sku: string; label?: string }[] | null;
};

const toGtmItem = (
  product: LandingProduct,
  positionIndex: number,
  itemListName: LandingItemListName
): GTMEcommerceItem => {
  const variant = product.configurableVariants?.[0];

  return {
    item_id: variant?.sku ?? product.sku ?? '',
    item_name:
      product.name ||
      (typeof product.heading === 'string' ? product.heading : ''),
    price: String(product.finalPrice ?? product.basePrice ?? 0),
    item_brand: product.brand || null,
    item_category: product.series || product.brand || null,
    variant: variant?.label ?? product.volume ?? null,
    item_list_name: itemListName,
    position: positionIndex + 1
  };
};

const normalizeLandingSlug = (pathname: string) => {
  const trimmed = pathname.replace(/^\/+|\/+$/g, '');
  const first = trimmed.split('/')[0] || '';

  return first ? first.toLowerCase() : 'unknown';
};

export function useLandingProductGTM(
  product?: LandingProduct | null,
  positionIndex = 0
): {
  ref: RefObject<HTMLDivElement | null>;
  onSelectItem: () => void;
  viewItemList: ReactElement | null;
} {
  const ref = useRef<HTMLDivElement | null>(null);
  const pathname = usePathname();
  const itemListName: LandingItemListName = `landing-${normalizeLandingSlug(
    pathname
  )}`;
  const item = product?.sku
    ? toGtmItem(product, positionIndex, itemListName)
    : null;

  const onSelectItem = () => {
    if (!item) return;
    sendGTMEventEcommerce({
      event: 'select_item',
      ecommerce: { items: [item] }
    });
  };

  const viewItemList = item ? (
    <GTMViewItemList
      refItem={ref}
      item={item}
    />
  ) : null;

  return { ref, onSelectItem, viewItemList };
}
